import { Checkbox } from '@/components/ui/checkbox';
import { Typo } from '@/components/ui/typography';
import { CartServiceItem } from '@/components/web/cart/cart-service-item';
import { UserCartItem, useUserGlobalCart } from '@/hooks/use-user-global-cart';
import { cn } from '@/lib/utils';
import { useMemo, useState } from 'react';
import { ServiceCartDrawer } from './service-cart-drawer';
// Custom ServiceGroup component with batch selection support
interface CartServiceGroupProps {
  siteName: string;
  date: string;
  items: UserCartItem[];
  selectedItems: Set<string>;
  onItemSelectionChange: (itemId: string, selected: boolean) => void;
  onGroupSelectionChange: (itemIds: string[], selected: boolean) => void;
  onItemRemove: (itemId: string) => Promise<void>;
  onUpdateQuantity: (rowId: string, quantity: number) => void;
  disabled?: boolean;
  className?: string;
}

export function CartServiceGroup({
  siteName,
  date,
  items,
  selectedItems,
  onItemSelectionChange,
  onGroupSelectionChange,
  onItemRemove,
  onUpdateQuantity = () => {},
  disabled = false,
  className = '',
}: CartServiceGroupProps) {
  const { selectedSessions } = useUserGlobalCart();
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<UserCartItem | null>(null);

  const allItemsSelected = items.every((item) => selectedItems.has(item.id));
  const someItemsSelected = items.some((item) => selectedItems.has(item.id));

  const handleGroupSelectionChange = (selected: boolean) => {
    const itemIds = items.map((item) => item.id);
    onGroupSelectionChange(itemIds, selected);
  };

  const handleItemRemove = async (itemId: string) => {
    await onItemRemove(itemId);
  };

  // Handle edit - fetch the full rate data from cart endpoint with API-validated services
  const handleEditItem = async (item: UserCartItem) => {
    setSelectedItem(item);
    setIsEditDrawerOpen(true);
  };

  const groupDisabled = useMemo(() => {
    if (items.every((item) => !item.options.has_inventory)) {
      return disabled;
    }
    const itemsNeedSession = items.filter((item) => item.options.has_inventory);
    return itemsNeedSession.some((item) => !selectedSessions[item.rowId]) || disabled;
  }, [items, disabled, selectedSessions]);

  return (
    <div className={cn('space-y-4', className)}>
      {/* Group Header */}
      <div
        className={cn(
          'flex items-center justify-between rounded-lg bg-gray-50 p-4',
          (someItemsSelected || allItemsSelected) && 'bg-red-100/10 shadow',
        )}
      >
        <div className="flex items-center gap-3">
          <Checkbox
            checked={allItemsSelected}
            ref={(ref) => {
              if (ref) {
                const input = ref.querySelector('input[type="checkbox"]') as HTMLInputElement;
                if (input) {
                  input.indeterminate = someItemsSelected && !allItemsSelected;
                }
              }
            }}
            onCheckedChange={handleGroupSelectionChange}
            disabled={groupDisabled}
            className="size-6"
          />
          <div>
            <div className="flex items-center gap-2">
              {/* <MapPin className="h-4 w-4 text-primary" /> */}
              <Typo variant="h6" className="font-semibold text-gray-900">
                {siteName} - {date}
              </Typo>
            </div>

            <Typo variant="small" className="text-gray-600 md:hidden">
              {items.length} dịch vụ
            </Typo>
          </div>
        </div>
        <Typo variant="small" className="hidden text-gray-600 md:block">
          {items.length} dịch vụ
        </Typo>
      </div>

      {/* Group Items */}
      <div className="space-y-3 pl-6">
        {items.map((item) => (
          <CartServiceItem
            key={item.id}
            item={item}
            selected={selectedItems.has(item.id)}
            onSelectionChange={(selected) => onItemSelectionChange(item.id, selected)}
            onRemove={() => handleItemRemove(item.id)}
            onUpdateQuantity={(rowId, quantity) => onUpdateQuantity(rowId, quantity)}
            disabled={disabled}
            showEditButton={true}
            onEdit={handleEditItem}
          />
        ))}
      </div>

      {/* Service Cart Drawer for editing */}
      {selectedItem && (
        <ServiceCartDrawer
          open={isEditDrawerOpen}
          onOpenChange={(open) => {
            setIsEditDrawerOpen(open);
            if (!open) {
              setSelectedItem(null);
            }
          }}
          cartItem={selectedItem}
          searchParams={{
            site: selectedItem.options?.site_mapping_code || '',
            date: selectedItem.options?.date || '',
          }}
        />
      )}
    </div>
  );
}
